import { useState, useMemo, useEffect, useRef } from "react";
import {
  Activity,
  Calendar,
  Filter,
  Users,
  PieChart,
  BarChart3,
  Clock,
} from "lucide-react";
import StatsCards from "./JourneyPaths/StatsCards";
import TransitionGraph from "./JourneyPaths/TransitionGraph";
import TransitionMatrix from "./JourneyPaths/TransitionMatrix";
import DwellTimesChart from "./JourneyPaths/DwellTimesChart";
import IndividualReid from "./JourneyPaths/IndividualReid";

const JourneyPaths = ({ journeyData = {}, selectedYear = "2025", selectedSite, onDateRangeChange, initialDateRange }) => {
  const [expandedReids, setExpandedReids] = useState({});
  const [sortBy, setSortBy] = useState("recent");
  const [activeSection, setActiveSection] = useState("overview");

  // Extract zone metadata from journey data
  const zoneMetadata = useMemo(() => {
    return journeyData.zoneMetadata || {
      zoneIdToName: {},
      zoneNameToId: {},
      zones: []
    };
  }, [journeyData.zoneMetadata]);

  // Process journey data
  const dwellPathsByReid = journeyData.dwellPathsByReid || {};
  const currentData = journeyData;

  const journeyStats = useMemo(() => {
    const stats = {};
    Object.entries(dwellPathsByReid).forEach(([reid, journeys]) => {
      if (!journeys || journeys.length === 0) {
        stats[reid] = {
          journeyCount: 0,
          zoneCount: 0,
          totalDwellTime: 0,
          avgDwellTime: 0,
          mostVisitedZone: "none",
          mostRecent: 0,
        };
        return;
      }

      const zoneCounts = {};
      let totalDwellTime = 0;

      journeys.forEach((journey) => {
        zoneCounts[journey.zone_name] =
          (zoneCounts[journey.zone_name] || 0) + 1;
        totalDwellTime += journey.dwell_time || 0;
      });

      const mostRecent = Math.max(...journeys.map((j) => j.timestamp * 1000));
      const mostVisitedZone =
        Object.entries(zoneCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ||
        "none";

      stats[reid] = {
        journeyCount: journeys.length,
        zoneCount: Object.keys(zoneCounts).length,
        totalDwellTime,
        avgDwellTime: totalDwellTime / journeys.length,
        mostVisitedZone,
        mostRecent,
      };
    });
    return stats;
  }, [dwellPathsByReid]);

  const globalStats = useMemo(() => {
    const totalReids = Object.keys(dwellPathsByReid).length;
    const totalJourneys = Object.values(journeyStats).reduce(
      (sum, stat) => sum + stat.journeyCount,
      0
    );
    const totalDwellTime = Object.values(journeyStats).reduce(
      (sum, stat) => sum + stat.totalDwellTime,
      0
    );
    const uniqueZones = new Set();

    Object.values(dwellPathsByReid).forEach((journeys) => {
      journeys.forEach((journey) => uniqueZones.add(journey.zone_name));
    });

    return {
      totalReids,
      totalJourneys,
      totalDwellTime,
      avgDwellTime: totalJourneys > 0 ? totalDwellTime / totalJourneys : 0,
      uniqueZones: uniqueZones.size,
    };
  }, [dwellPathsByReid, journeyStats]);

  const sortedReids = useMemo(() => {
    return Object.keys(dwellPathsByReid).sort((a, b) => {
      if (sortBy === "recent") {
        return (
          (journeyStats[b]?.mostRecent || 0) -
          (journeyStats[a]?.mostRecent || 0)
        );
      } else if (sortBy === "mostActive") {
        return (
          (journeyStats[b]?.journeyCount || 0) -
          (journeyStats[a]?.journeyCount || 0)
        );
      } else if (sortBy === "longestDwell") {
        return (
          (journeyStats[b]?.totalDwellTime || 0) -
          (journeyStats[a]?.totalDwellTime || 0)
        );
      }
      return 0;
    });
  }, [dwellPathsByReid, journeyStats, sortBy]);

  const toggleExpand = (reid) => {
    setExpandedReids((prev) => ({
      ...prev,
      [reid]: !prev[reid],
    }));
  };

  const sections = [
    { id: "overview", label: "Overview", icon: PieChart },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
    { id: "individuals", label: "Individual REIDs", icon: Users },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case "overview":
        return (
          <div className="reid-section-spacing">
            <StatsCards globalStats={globalStats} />
            <div>
              {currentData.zoneTransitionProbabilities && (
                <TransitionGraph
                  transitions={currentData.zoneTransitionProbabilities}
                  zoneMetadata={zoneMetadata}
                  hw_id={selectedSite?.hwid || ""}
                />
              )}
            </div>
          </div>
        );

      case "analytics":
        return (
          <div className="reid-section-spacing-small">
            {currentData.averageDwellTimePerZone && (
              <DwellTimesChart
                dwellTimes={currentData.averageDwellTimePerZone}
              />
            )}
            {currentData.zoneTransitionProbabilities && (
              <TransitionMatrix
                transitions={currentData.zoneTransitionProbabilities}
              />
            )}
          </div>
        );

      case "individuals":
        return (
          <div className="reid-section-spacing-small">
            <div className="reid-sort-container">
              <div className="reid-sort-header">
                <h4 className="reid-sort-title">
                  <Filter size={18} className="reid-card-header-icon" />
                  Sort Options
                </h4>
                <span className="reid-sort-count">
                  {Object.keys(dwellPathsByReid).length} REIDs
                </span>
              </div>
              <div className="reid-sort-buttons">
                {[
                  { key: "recent", label: "Most Recent" },
                  { key: "mostActive", label: "Most Active" },
                  { key: "longestDwell", label: "Longest Dwell" },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    className={`reid-sort-button ${
                      sortBy === key
                        ? "reid-sort-button-active"
                        : "reid-sort-button-inactive"
                    }`}
                    onClick={() => setSortBy(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {Object.keys(dwellPathsByReid).length === 0 ? (
              <div className="reid-empty-state">
                <Users size={64} className="reid-empty-state-icon" />
                <h3 className="reid-empty-state-title">
                  No Journey Data
                </h3>
                <p className="reid-empty-state-text">
                  No journey data found for the selected date range. Check the browser console for debug information.
                </p>
                <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: '1rem' }}>
                  Debug: dwellPathsByReid has {Object.keys(dwellPathsByReid).length} keys
                </div>
                <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                  Zone metadata: {Object.keys(zoneMetadata.zoneIdToName || {}).length} zones
                </div>
              </div>
            ) : (
              <div className="reid-section-spacing">
                {sortedReids.map((reid) => (
                  <IndividualReid
                    key={reid}
                    reid={reid}
                    journeys={dwellPathsByReid[reid] || []}
                    stats={journeyStats[reid] || {}}
                    isExpanded={expandedReids[reid]}
                    onToggle={() => toggleExpand(reid)}
                  />
                ))}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="reid-container">
      <div className="reid-max-container">
        {/* Replace the entire date range section with the compact component */}
        <div className="reid-nav-container" style={{zIndex: 999, overflow: 'visible', }}>
          {initialDateRange ? (
            <CompactDatePicker
              key={initialDateRange.startDate}
              selectedYear={selectedYear}
              onDateRangeChange={onDateRangeChange}
              initialStartDate={initialDateRange.startDate}
              initialEndDate={initialDateRange.endDate}
            />
          ) : (
            <div style={{ padding: '16px', color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
              Determining available data range...
            </div>
          )}
        </div>

        {/* Section Navigation */}
        <div className="reid-nav-container">
          <div className="reid-nav-tabs">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  className={`reid-nav-tab ${
                    activeSection === section.id
                      ? "reid-nav-tab-active"
                      : "reid-nav-tab-inactive"
                  }`}
                  onClick={() => setActiveSection(section.id)}
                >
                  <Icon size={18} />
                  <span>{section.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="reid-content-section">{renderContent()}</div>
      </div>
    </div>
  );
};

export default JourneyPaths;




const CompactDatePicker = ({
  selectedYear = "2025",
  onDateRangeChange,
  initialStartDate,
  initialEndDate,
}) => {
  const hasInitialDates = !!(initialStartDate && initialEndDate);
  const [dateRangeType, setDateRangeType] = useState(hasInitialDates ? "custom" : "thisMonth");
  const [customStartDate, setCustomStartDate] = useState(initialStartDate || "");
  const [customEndDate, setCustomEndDate] = useState(initialEndDate || "");
  const [showDropdown, setShowDropdown] = useState(false);
  const [customDateMode, setCustomDateMode] = useState("range");
  const [singleDate, setSingleDate] = useState("");
  
  const [dropdownPosition, setDropdownPosition] = useState({ top: '100%', left: '0' });
  
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Calculate dropdown position to avoid viewport cutoff
  useEffect(() => {
    if (showDropdown && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const dropdownWidth = 360;

      let left = '0';
      let top = '100%';

      // Check if dropdown would go off right edge
      if (buttonRect.right + dropdownWidth > viewportWidth) {
        left = 'auto';
        // Position dropdown to the left of the button
        setDropdownPosition({ 
          top: '100%', 
          left: 'auto',
          right: '0'
        });
      } else {
        setDropdownPosition({ 
          top: '100%', 
          left: '0'
        });
      }
    }
  }, [showDropdown]);


  // Helper functions for date calculations
  const getDateRanges = () => {
    const today = new Date();
    const year = parseInt(selectedYear);
    
    const currentDate = today.getFullYear() === year ? today : new Date(year, 11, 31);
    
    const formatDate = (date) => {
      return date.toISOString().split('T')[0];
    };

    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

    return {
      today: {
        start: formatDate(currentDate),
        end: formatDate(currentDate)
      },
      thisWeek: {
        start: formatDate(startOfWeek),
        end: formatDate(endOfWeek)
      },
      thisMonth: {
        start: formatDate(startOfMonth),
        end: formatDate(endOfMonth)
      }
    };
  };

  const dateRanges = getDateRanges();

  const getCurrentDateRange = () => {
    if (dateRangeType === "custom") {
      if (customDateMode === "single") {
        return {
          start: singleDate,
          end: singleDate
        };
      }
      return {
        start: customStartDate,
        end: customEndDate
      };
    }
    return dateRanges[dateRangeType];
  };

  const formatDateRange = () => {
    const range = getCurrentDateRange();
    if (!range.start || !range.end) return "";
    
    const startDate = new Date(range.start);
    const endDate = new Date(range.end);
    
    if (range.start === range.end) {
      return startDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
    }
    
    return `${startDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric"
    })} - ${endDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    })}`;
  };

  const calculateDaysDifference = () => {
    if (customDateMode === "single" && singleDate) {
      return 1;
    }
    if (customDateMode === "range" && customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      const diffTime = Math.abs(end - start);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    }
    return 0;
  };

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update parent component when date range changes (debounced to avoid firing on every intermediate state change)
  useEffect(() => {
    if (!onDateRangeChange) return;
    const range = getCurrentDateRange();
    if (!range.start || !range.end) return;

    const timer = setTimeout(() => {
      onDateRangeChange(range.start, range.end);
    }, 300);

    return () => clearTimeout(timer);
  }, [dateRangeType, customStartDate, customEndDate, singleDate, customDateMode]);

  const handleDateRangeChange = (type) => {
    setDateRangeType(type);
    if (type === "custom") {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  const handleCustomDateApply = () => {
    if (customDateMode === "single") {
      if (singleDate) {
        const year = new Date(singleDate).getFullYear();
        const targetYear = parseInt(selectedYear);
        
        if (year === targetYear) {
          setShowDropdown(false);
        } else {
          alert(`Please select a date within ${selectedYear}`);
        }
      }
    } else {
      if (customStartDate && customEndDate) {
        const startYear = new Date(customStartDate).getFullYear();
        const endYear = new Date(customEndDate).getFullYear();
        const targetYear = parseInt(selectedYear);
        
        if (startYear === targetYear && endYear === targetYear) {
          if (new Date(customStartDate) <= new Date(customEndDate)) {
            setShowDropdown(false);
          } else {
            alert("Start date must be before or equal to end date");
          }
        } else {
          alert(`Please select dates within ${selectedYear}`);
        }
      }
    }
  };

  const handleModeChange = (mode) => {
    setCustomDateMode(mode);
    if (mode === "single") {
      setCustomStartDate("");
      setCustomEndDate("");
    } else {
      setSingleDate("");
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h4 style={styles.title}>
          <Clock size={18} style={styles.titleIcon} />
          Date Range Selection
        </h4>
        <span style={styles.currentRange}>
          Current: {formatDateRange()}
        </span>
      </div>
      
      <div style={styles.controls}>
        <div style={styles.buttonGroup}>
          {[
            { key: "today", label: "Today" },
            { key: "thisWeek", label: "This Week" },
            { key: "thisMonth", label: "This Month" },
          ].map(({ key, label }) => (
            <button
              key={key}
              style={{
                ...styles.button,
                ...(dateRangeType === key ? styles.buttonActive : styles.buttonInactive)
              }}
              onClick={() => handleDateRangeChange(key)}
            >
              {label}
            </button>
          ))}
          
          <div style={styles.dropdownContainer}>
            <button
              ref={buttonRef}
              style={{
                ...styles.button,
                ...(dateRangeType === "custom" ? styles.buttonActive : styles.buttonInactive)
              }}
              onClick={() => {
                if (dateRangeType === "custom") {
                  setShowDropdown(!showDropdown);
                } else {
                  handleDateRangeChange("custom");
                }
              }}
            >
              Custom
            </button>
            
            {showDropdown && dateRangeType === "custom" && (
              <div 
                ref={dropdownRef} 
                style={{
                  ...styles.dropdown,
                  ...dropdownPosition
                }}
              >
                <div style={styles.dropdownContent}>
                  <div style={styles.selectionTabs}>
                    <div 
                      style={{
                        ...styles.tab,
                        ...(customDateMode === "single" ? styles.tabActive : {})
                      }}
                      onClick={() => handleModeChange("single")}
                    >
                      Single Date
                    </div>
                    <div 
                      style={{
                        ...styles.tab,
                        ...(customDateMode === "range" ? styles.tabActive : {})
                      }}
                      onClick={() => handleModeChange("range")}
                    >
                      Date Range
                    </div>
                  </div>
                  
                  {customDateMode === "single" ? (
                    <div style={styles.dateInputs}>
                      <div style={styles.dateGroup}>
                        <div style={styles.dateLabel}>Select Date</div>
                        <input
                          type="date"
                          style={styles.dateInput}
                          value={singleDate}
                          onChange={(e) => setSingleDate(e.target.value)}
                          min={`${selectedYear}-01-01`}
                          max={`${selectedYear}-12-31`}
                        />
                      </div>
                    </div>
                  ) : (
                    <div style={styles.dateInputsRange}>
                      <div style={styles.dateGroup}>
                        <div style={styles.dateLabel}>Start Date</div>
                        <input
                          type="date"
                          style={styles.dateInput}
                          value={customStartDate}
                          onChange={(e) => setCustomStartDate(e.target.value)}
                          min={`${selectedYear}-01-01`}
                          max={`${selectedYear}-12-31`}
                        />
                      </div>
                      <div style={styles.dateSeparator}>→</div>
                      <div style={styles.dateGroup}>
                        <div style={styles.dateLabel}>End Date</div>
                        <input
                          type="date"
                          style={styles.dateInput}
                          value={customEndDate}
                          onChange={(e) => setCustomEndDate(e.target.value)}
                          min={customStartDate || `${selectedYear}-01-01`}
                          max={`${selectedYear}-12-31`}
                        />
                      </div>
                    </div>
                  )}
                  
                  {((customDateMode === "single" && singleDate) || 
                    (customDateMode === "range" && customStartDate && customEndDate)) && (
                    <div style={styles.selectedInfo}>
                      <div style={styles.selectedRangeText}>
                        {customDateMode === "single" 
                          ? new Date(singleDate).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric", 
                              year: "numeric"
                            })
                          : `${new Date(customStartDate).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric"
                            })} - ${new Date(customEndDate).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric"
                            })}`
                        }
                      </div>
                      <div style={styles.selectedDays}>
                        {calculateDaysDifference()} day{calculateDaysDifference() !== 1 ? 's' : ''} selected
                      </div>
                    </div>
                  )}
                  
                  <div style={styles.footer}>
                    <button
                      style={styles.buttonSecondary}
                      onClick={() => setShowDropdown(false)}
                    >
                      Cancel
                    </button>
                    <button
                      style={{
                        ...styles.buttonPrimary,
                        ...(((customDateMode === "single" && !singleDate) || 
                             (customDateMode === "range" && (!customStartDate || !customEndDate))) 
                            ? styles.buttonDisabled : {})
                      }}
                      onClick={handleCustomDateApply}
                      disabled={
                        customDateMode === "single" 
                          ? !singleDate 
                          : !customStartDate || !customEndDate
                      }
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    marginBottom: '20px',
    padding: '16px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
  },
  titleIcon: {
    color: '#a5b4fc',
  },
  currentRange: {
    padding: '8px 16px',
    background: 'rgba(79, 70, 229, 0.2)',
    border: '1px solid rgba(79, 70, 229, 0.3)',
    borderRadius: '6px',
    color: '#a5b4fc',
    fontSize: '14px',
  },
  controls: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  button: {
    padding: '8px 16px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s ease',
    background: 'transparent',
  },
  buttonActive: {
    background: '#4f46e5',
    borderColor: '#4f46e5',
    color: 'white',
  },
  buttonInactive: {
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
  },
  dropdownContainer: {
    position: 'relative',
    display: 'inline-block',
    isolation: 'isolate',
  },
  dropdown: {
    position: 'absolute',
    marginTop: '8px',
    background: '#2d3561',
    borderRadius: '12px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    width: '360px',
    zIndex: 999,
    maxHeight: '400px',
    overflow: 'visible',
  },
  dropdownContent: {
    padding: '20px 24px',
  },
  selectionTabs: {
    display: 'flex',
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '8px',
    padding: '4px',
    marginBottom: '20px',
  },
  tab: {
    flex: 1,
    padding: '8px 16px',
    textAlign: 'center',
    borderRadius: '6px',
    cursor: 'pointer',
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '14px',
    transition: 'all 0.2s ease',
  },
  tabActive: {
    background: '#4f46e5',
    color: 'white',
  },
  dateInputs: {
    marginBottom: '16px',
  },
  dateInputsRange: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    gap: '12px',
    alignItems: 'end',
    marginBottom: '16px',
  },
  dateGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  dateLabel: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: '12px',
    fontWeight: '500',
  },
  dateInput: {
    padding: '10px 12px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '6px',
    color: 'white',
    fontSize: '14px',
    outline: 'none',
    transition: 'all 0.2s ease',
  },
  dateSeparator: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: '18px',
    textAlign: 'center',
    marginBottom: '8px',
  },
  selectedInfo: {
    padding: '12px 16px',
    background: 'rgba(34, 197, 94, 0.15)',
    border: '1px solid rgba(34, 197, 94, 0.3)',
    borderRadius: '8px',
    marginBottom: '20px',
  },
  selectedRangeText: {
    color: '#86efac',
    fontSize: '14px',
    fontWeight: '500',
    marginBottom: '2px',
  },
  selectedDays: {
    color: 'rgba(134, 239, 172, 0.8)',
    fontSize: '12px',
  },
  footer: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  buttonSecondary: {
    padding: '10px 20px',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    background: 'transparent',
    color: 'rgba(255, 255, 255, 0.8)',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s ease',
  },
  buttonPrimary: {
    padding: '10px 20px',
    background: '#4f46e5',
    border: '1px solid #4f46e5',
    color: 'white',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  buttonDisabled: {
    opacity: '0.5',
    cursor: 'not-allowed',
  },
};